import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import TempleCard from '../components/TempleCard';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import DynamicAdBlock from '../components/DynamicAdBlock';
import OptimizedImage from '../components/OptimizedImage';
import { getTemples, getOtherTemples, getSettings, getBlogs } from '../services/api';
import { generateHomeItemListSchema, generateGlobalSchema } from '../utils/schemaGenerator';

const Home = () => {
  const [temples, setTemples] = useState([]);
  const [otherTemples, setOtherTemples] = useState([]);
  const [settings, setSettings] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templeData, otherTempleData, settingsData, blogsData] = await Promise.all([
          getTemples(),
          getOtherTemples(),
          getSettings(),
          getBlogs('published')
        ]);
        setTemples(templeData);
        setOtherTemples(otherTempleData);
        setSettings(settingsData);
        setBlogs(blogsData);
      } catch (error) {
        console.error("Failed to fetch homepage data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredTemples = temples.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOtherTemples = otherTemples.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const homeSchema = generateHomeItemListSchema(temples);
  const globalSchema = generateGlobalSchema();
  const rawSchemas = [...globalSchema, homeSchema].filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-stone-50"
    >
      <SEO
        title="Nath Nagri Bareilly | 7 Nath Mandir Bareilly | Nathnagri Temples, History & Darshan"
        description="Nath Nagri Bareilly में 7 प्रमुख Nath Mandir की पूरी जानकारी पाएँ"
        schema={rawSchemas}
      />

      <AdBanner />

      {/* Hero Section */}
      <section className="relative w-full h-[500px] flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/images/HeroBanner_nath-nagri-bareilly-com.png"
            alt="Nath Nagri Darshan"
            className="w-full h-full object-cover"
            fallback="/images/HeroBanner_nath-nagri-bareilly-com.png"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative z-10 max-w-4xl px-4 text-white mt-12">
          <h1 className="text-5xl md:text-6xl font-extrabold text-saffron-500 mb-4 drop-shadow-lg">
            नाथ नगरी <span className="text-white">दर्शन</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-200 mb-8 font-medium">
            बरेली के प्रसिद्ध मंदिरों, 7 नाथ मंदिरों, शिव मंदिरों और प्रमुख धार्मिक व पर्यटन स्थलों का अन्वेषण करें, जो इस पवित्र नगरी की आध्यात्मिक पहचान को दर्शाते हैं।
          </p>
          <Link to="/temples" className="inline-flex items-center px-8 py-3 bg-saffron-600 hover:bg-saffron-700 text-white font-bold rounded-full transition-colors shadow-lg">
            मंदिर देखें <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-4 py-8">

        {/* About Us & Ad Section */}
        <section className="py-12 border-b border-stone-200 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-extrabold text-stone-800 mb-6">
                नाथ नगरी <span className="text-saffron-600">में आपका स्वागत है</span>
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                बरेली शहर को "नाथ नगरी" के रूप में जाना जाता है क्योंकि इसके चारों कोनों और मध्य में भगवान शिव के सात प्राचीन सिद्धपीठ स्थित हैं। ये मंदिर हमारी आध्यात्मिक और सांस्कृतिक विरासत के प्रतीक हैं। हर साल लाखों भक्त यहाँ दर्शन के लिए आते हैं, विशेषकर सावन मास और शिवरात्रि के पावन पर्व पर।
              </p>
              <Link to="/about" className="inline-flex items-center text-saffron-600 font-bold hover:text-saffron-700 transition-colors">
                विस्तार से पढ़ें <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-1">
              <AdBanner placement="sidebar" pageName="home" />
            </div>
          </div>
        </section>

        {/* <div className="mb-8 relative max-w-2xl mx-auto">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-saffron-500 focus:border-saffron-500 shadow-sm transition-shadow"
            placeholder="Search temples..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div> */}

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-stone-800 mb-6">प्रमुख नाथ मंदिर</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemples.map((temple) => (
              <TempleCard key={temple.id} temple={temple} />
            ))}
          </div>
        </section>

        {filteredOtherTemples.length > 0 && (
          <section className="py-12 mb-12 bg-[#F6F6F6]">
            <div className="max-w-[1200px] mx-auto">
              <h2 className="text-3xl font-bold text-stone-800 mb-8">अन्य नाथ नगरी मंदिर (Other NathNagri Temples)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOtherTemples.slice(0, 3).map((temple) => (
                  <TempleCard key={temple.id} temple={temple} />
                ))}
              </div>
              <div className="mt-10 flex justify-center">
                <Link to="/temples" className="px-6 py-3 bg-white border border-gray-200 text-saffron-600 font-bold rounded-full hover:shadow-md transition-shadow flex items-center">
                  सभी अन्य मंदिर देखें (View All) <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </section>
        )}

        <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.middle} />

        {blogs.length > 0 && (
          <section className="py-12 bg-white/50 rounded-2xl mt-12">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex justify-between items-end mb-8">
                <div>
                  <h2 className="text-3xl font-black text-stone-800 tracking-tight">Recent Articles</h2>
                  <p className="text-stone-500 mt-2">Discover stories and insights</p>
                </div>
                <Link to="/blogs" className="hidden sm:flex items-center text-saffron-600 font-bold hover:text-saffron-700 transition-colors">
                  View All <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {blogs.slice(0, 3).map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug || blog.id}`}
                    className="group flex flex-col bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 hover:border-saffron-300 relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-saffron-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    <div>
                      <div className="mb-3 text-xs font-bold text-saffron-600 tracking-wider uppercase flex items-center">
                        <span className="w-2 h-2 rounded-full bg-saffron-500 mr-2"></span>
                        {blog.date} &bull; {blog.author}
                      </div>
                      <h3 className="text-xl font-extrabold text-stone-800 mb-3 group-hover:text-saffron-600 transition-colors leading-snug">
                        {blog.title}
                      </h3>
                      <p className="text-stone-600 line-clamp-3 mb-6 text-sm leading-relaxed">
                        {blog.excerpt || blog.content.replace(/<[^>]+>/g, '')}
                      </p>
                    </div>

                    <div className="mt-auto text-saffron-600 font-bold flex items-center group-hover:underline decoration-saffron-500 decoration-2 underline-offset-4 text-sm">
                      पूरा पढ़ें <ArrowRight className="w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <DynamicAdBlock adConfig={settings?.homepageAdsConfig?.blogs} />
      </main>
    </motion.div>
  );
};

export default Home;
